import React, { useEffect, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { Button } from "../../../ui/button.js"
import { useIsMobile } from "../../../../hooks/use-mobile.js"
import { ChevronLeft, ChevronRight, LogOut, Menu, X } from "lucide-react"
import { FOOTER_ITEMS, MENU_ITEMS } from "../constants/index.js"
import { getPermissionsFromStorage, hasPermission, isAdmin } from "../utils/permissions.js"
import logoImage from "../../../../../assets/img/nova-logo-branca.png"
import { cn } from "../../../../lib/utils.js"
import { useNavigation } from "../../../../hooks/use-navigation.js"
import useAuth from "../../../../../hooks/useAuth.js"

/**
 * Componente Sidebar
 * @param {Object} props - Props do componente
 * @returns {React.ReactElement} Componente Sidebar
 */
export function Sidebar({
  activeMenu,
  setActiveMenu,
  sidebarOpen,
  setSidebarOpen,
  collapsed,
  setCollapsed,
}) {
  const location = useLocation()
  const pathname = location.pathname
  const isMobile = useIsMobile()
  const { navigate } = useNavigation()
  const { signout } = useAuth()
  const [permissions, setPermissions] = useState(getPermissionsFromStorage())
  const [isCollapsedLogoHovered, setIsCollapsedLogoHovered] = useState(false)

  useEffect(() => {
    const storedPermissions = getPermissionsFromStorage()
    setPermissions(storedPermissions)
  }, [])

  // Body scroll lock for mobile sidebar
  useEffect(() => {
    if (isMobile) {
      if (sidebarOpen) {
        document.body.style.overflow = 'hidden'
        document.body.style.position = 'fixed'
        document.body.style.width = '100%'
      } else {
        document.body.style.overflow = ''
        document.body.style.position = ''
        document.body.style.width = ''
      }
    }

    return () => {
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
    }
  }, [sidebarOpen, isMobile])

  const filteredMenuItems = MENU_ITEMS.filter((item) => {
    if (item.requiresAdmin) {
      return isAdmin(permissions)
    }

    if (item.permission) {
      return hasPermission(permissions, item.permission, item.action || "islist")
    }

    return true
  })

  const handleNavigation = (href, label) => {
    if (pathname !== href) {
      setSidebarOpen(false)
      navigate(href)
    }
  }

  const handleOverlayClick = () => {
    setSidebarOpen(false)
  }

  const handleSidebarClick = (e) => {
    // Prevent closing when clicking inside the sidebar
    e.stopPropagation()
  }

  const handleLogout = () => {
    signout?.()
    setSidebarOpen(false)
  }

  const sidebarEnhancedStyles = `
    @media (min-width: 1024px) {
      .sidebar-nav {
        scrollbar-width: none;
        -ms-overflow-style: none;
      }
      .sidebar-nav::-webkit-scrollbar {
        display: none;
      }
    }
  `

  return (
    <>
      <style>{sidebarEnhancedStyles}</style>
      {/* {isMobile && !sidebarOpen && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden fixed left-4 top-4 h-10 w-10 rounded-lg bg-black text-white shadow-lg shadow-black/20 hover:bg-gray-900"
          style={{ 
            zIndex: 9999,
            position: 'fixed',
            top: '16px',
            left: '16px'
          }}
        >
          <Menu size={20} />
        </Button>
      )} */}

      {isMobile && (
        <div
          className="sidebar-overlay fixed inset-0 z-[1399] bg-black/60 lg:hidden"
          data-open={sidebarOpen}
          onClick={handleOverlayClick}
          style={{ 
            zIndex: sidebarOpen ? 1399 : -1,
            opacity: sidebarOpen ? 1 : 0,
            visibility: sidebarOpen ? 'visible' : 'hidden',
            pointerEvents: sidebarOpen ? 'auto' : 'none',
            transition: 'opacity 250ms ease, visibility 250ms ease'
          }}
        />
      )}

      <div
        className={cn(
          "sidebar-panel flex h-screen flex-col bg-black text-white",
          isMobile ? "fixed top-0 left-0 w-64" : "sticky top-0",
          collapsed && !isMobile ? "w-16" : "w-64",
          sidebarOpen || !isMobile ? "translate-x-0" : "-translate-x-full"
        )}
        style={{
          zIndex: isMobile ? (sidebarOpen ? 1400 : -1) : 1500,
          transition: 'transform 320ms cubic-bezier(0.4, 0, 0.2, 1), width 240ms cubic-bezier(0.4, 0, 0.2, 1), opacity 220ms ease'
        }}
        data-open={sidebarOpen || !isMobile}
        onClick={handleSidebarClick}
      >
        <div
          className={cn(
            "flex flex-shrink-0 items-center border-b border-gray-800 transition-all duration-200",
            collapsed && !isMobile ? "px-3 py-3" : "px-4 py-3"
          )}
        >
          {(!collapsed || isMobile) ? (
            <div className="flex w-full items-center justify-between">
              <img
                src={logoImage}
                alt="TREND Logo"
                className="h-12 w-auto object-contain"
              />
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCollapsed(true)}
                  className="hidden h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:bg-gray-900 hover:text-white lg:flex"
                >
                  <ChevronLeft size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSidebarOpen(false)}
                  className="h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:bg-gray-900 hover:text-white lg:hidden"
                >
                  <X size={16} />
                </Button>
              </div>
            </div>
          ) : (
            <div
              className="group relative flex w-full items-center justify-center"
              onMouseEnter={() => setIsCollapsedLogoHovered(true)}
              onMouseLeave={() => setIsCollapsedLogoHovered(false)}
              onFocus={() => setIsCollapsedLogoHovered(true)}
              onBlur={() => setIsCollapsedLogoHovered(false)}
              tabIndex={-1}
            >
              <img
                src={logoImage}
                alt="TREND Logo"
                className={cn(
                  "h-12 w-auto object-contain transition-opacity",
                  isCollapsedLogoHovered && "opacity-0"
                )}
                aria-hidden={isCollapsedLogoHovered}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCollapsed(false)}
                className={cn(
                  "absolute hidden h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-opacity hover:bg-gray-900 hover:text-white lg:flex",
                  isCollapsedLogoHovered ? "opacity-100" : "opacity-0 pointer-events-none"
                )}
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          )}
        </div>

        <nav className="sidebar-nav flex-1 overflow-y-auto px-3 py-1">
          <div className="flex flex-col gap-1">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon
              const isActive = pathname === item.href || (item.href === "/" && pathname === "/")

              return (
                <Link key={item.id} to={item.href}>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setActiveMenu(item.id)
                      handleNavigation(item.href, item.label)
                    }}
                    className={cn(
                      "relative flex h-10 w-full items-center rounded-lg text-[13px] font-medium text-gray-400 transition-colors hover:bg-gray-900 hover:text-white",
                      collapsed && !isMobile ? "justify-center px-0" : "justify-start gap-2.5 px-3",
                      isActive && "border-l-2 border-white bg-gray-800 text-white"
                    )}
                    title={collapsed && !isMobile ? item.label : undefined}
                  >
                    <Icon size={20} className="flex-shrink-0" />
                    {/* TODO - Implementar modulo de notificações */}
                    {(!collapsed || isMobile) && (
                      <>
                        <span className="truncate leading-tight">{item.label}</span>
                        {/* {item.hasIndicator && (
                          <span className="ml-auto h-1.5 w-1.5 rounded-full bg-red-500" />
                        )} */}
                      </>
                    )}
                    {/* {item.hasIndicator && collapsed && !isMobile && (
                      <span className="hidden h-1.5 w-1.5 rounded-full bg-red-500 lg:block absolute top-2 right-2" />
                    )} */}
                  </Button>
                </Link>
              )
            })}
          </div>
        </nav>

        <div className="border-t border-gray-800 px-2 py-2">
          <div className="flex flex-col gap-0.5">
            {FOOTER_ITEMS.map((item) => {
              const Icon = item.icon
              return (
                <Link key={item.id || item.href} to={item.href}>
                  <Button
                    variant="ghost"
                    onClick={() => handleNavigation(item.href, item.label)}
                    className={cn(
                      "flex h-10 w-full items-center rounded-lg text-[13px] font-medium text-gray-400 transition-colors hover:bg-gray-900 hover:text-white",
                      collapsed && !isMobile ? "justify-center px-0" : "justify-start gap-2.5 px-3"
                    )}
                    title={collapsed && !isMobile ? item.label : undefined}
                  >
                    <Icon size={20} className="flex-shrink-0" />
                    {(!collapsed || isMobile) && (
                      <span className="truncate leading-tight">{item.label}</span>
                    )}
                  </Button>
                </Link>
              )
            })}
          </div>

          <div className="mt-2 border-t border-gray-800 pt-2">
            <Button
              variant="ghost"
              onClick={handleLogout}
              className={cn(
                "flex h-10 w-full items-center rounded-lg text-[13px] font-semibold transition-colors text-gray-400 hover:bg-gray-900 hover:text-white",
                collapsed && !isMobile ? "justify-center px-0" : "justify-start gap-2.5 px-3"
              )}
              title={collapsed && !isMobile ? "Sair" : undefined}
            >
              <LogOut size={20} className="flex-shrink-0" />
              {(!collapsed || isMobile) && (
                <span className="truncate leading-tight">Sair</span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
